import React, { useState } from 'react';
import { Award, ChevronRight, Code } from 'lucide-react';
import Navbar from '../navbar/navbar.jsx';
import Footer from '../footer/footer.jsx';
import AnimatedSection from '../components/AnimatedSection.jsx';
import CertificateViewer from '../components/CertificateViewer.jsx';
import { conocimientosTranslations } from './translations.js';

const Conocimientos = ({ language, setLanguage, isDarkMode, setIsDarkMode }) => {
  const t = conocimientosTranslations[language];
  const [isCertificateViewerOpen, setIsCertificateViewerOpen] = useState(false);
  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-stone-200 text-gray-900'
    }`} style={{ fontFamily: "Raleway, sans-serif" }}>      <link
        href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Raleway:wght@300;400;500;600;700&display=swap"
        rel="stylesheet"
      />      <style>{`
        @keyframes waveAnimation {
          0% {
            d: path('M0,40 Q300,10 600,40 T1200,40 L1200,80 L0,80 Z');
          }
          50% {
            d: path('M0,40 Q300,65 600,40 T1200,40 L1200,80 L0,80 Z');
          }
          100% {
            d: path('M0,40 Q300,10 600,40 T1200,40 L1200,80 L0,80 Z');
          }
        }
        .animate-wave-path {
          animation: waveAnimation 5s ease-in-out infinite;
        }
      `}</style>
      
      <Navbar 
        language={language} 
        setLanguage={setLanguage}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
      />
      
      {/* Main Content */}
      <main className="pt-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto py-12">
          {/* Education Section */}
          <AnimatedSection animationType="fadeInUp">
            <section className="mb-16">
              <h1 
                className={`text-4xl md:text-6xl font-bold mb-6 transition-colors duration-300 ${
                  isDarkMode ? 'text-red-400' : 'text-red-800'
                }`}
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                {t.title}
              </h1>
              <p className={`text-lg leading-relaxed mb-8 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {t.description}
              </p>
              <button 
                onClick={() => {
                  const element = document.getElementById('technologies');
                  if (element) {
                    const elementPosition = element.getBoundingClientRect().top;
                    // Ajusta el offset a -160 para que el scroll deje el título aún más arriba
                    const offsetPosition = elementPosition + window.pageYOffset - 160;
                    
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }}
                className={`px-8 py-3 rounded-full font-semibold hover:-translate-y-1 transition-all duration-300 flex items-center gap-2 ${
                  isDarkMode 
                    ? 'bg-red-900 text-white hover:bg-red-800' 
                    : 'bg-red-900 text-white hover:bg-red-800'
                }`}
              >
                {t.technologies.button}
                <ChevronRight className="w-5 h-5" />
              </button>
            </section>
          </AnimatedSection>

          {/* Education Cards */}
          <section className="mb-20">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Animated cards */}
              <AnimatedSection animationType="fadeInUp" delay={200}>
                {/* Facultad Card */}
                <div className={`rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105 ${
                  isDarkMode ? 'bg-gray-800 shadow-gray-900/50' : 'bg-white shadow-gray-200'
                }`}>
                  <div className="h-64 flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300">
                    <img 
                      src="/img/facultad.png" 
                      alt="Facultad de Ingeniería"
                      className="w-40 h-40 object-contain"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {t.facultad.title}
                    </h3>
                    <p className={`text-sm leading-relaxed transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {t.facultad.description}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
              
              <AnimatedSection animationType="fadeInUp" delay={400}>
                {/* JaP Card */}
                <div className={`rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105 ${
                  isDarkMode ? 'bg-gray-800 shadow-gray-900/50' : 'bg-white shadow-gray-200'
                }`}>                <div className="h-64 flex items-center justify-center bg-gradient-to-br from-orange-100 to-orange-200">
                  <img 
                    src="/img/JaP.png" 
                    alt="Jóvenes a Programar"
                    className="w-40 h-40 object-contain"
                  />
                </div>
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {t.jap.title}
                  </h3>
                  <p className={`text-sm leading-relaxed transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {t.jap.description}
                  </p>
                </div>
              </div>
              </AnimatedSection>
              
              <AnimatedSection animationType="fadeInUp" delay={600}>
                {/* Coursera Card */}
                <div className={`rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105 ${
                  isDarkMode ? 'bg-gray-800 shadow-gray-900/50' : 'bg-white shadow-gray-200'
                }`}>
                  <div className="h-64 flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
                    <img 
                      src="/img/Coursera.png"
                      alt="Coursera"
                      className="w-40 h-40 object-contain"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {t.coursera.title}
                    </h3>
                    <p className={`text-sm leading-relaxed transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {t.coursera.description}
                    </p>
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection animationType="fadeInUp" delay={800}>
                {/* Google Cloud Card */}
                <div className={`rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105 ${
                  isDarkMode ? 'bg-gray-800 shadow-gray-900/50' : 'bg-white shadow-gray-200'
                }`}>
                  <div className="h-64 flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
                    <img 
                      src="/img/GoogleCloud.png"
                      alt="Google Cloud"
                      className="w-40 h-40 object-contain"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {t.googlecloud.title}
                    </h3>
                    <p className={`text-sm leading-relaxed transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {t.googlecloud.description}
                    </p>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </section>
        </div>      </main>      {/* Wave Separator - Full Width */}
      <div className="relative w-full overflow-hidden -mb-1">
        <svg className="w-full h-16" viewBox="0 0 1200 80" preserveAspectRatio="none">
          <path 
            className="animate-wave-path"
            d="M0,40 Q300,10 600,40 T1200,40 L1200,80 L0,80 Z" 
            fill="#7F1D1D"
          />
        </svg>
      </div>

      {/* Certificates Section - Full Width */}
      <section className="bg-red-900 w-full text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-4 mb-6">
            <Award className="w-12 h-12 text-white" />
            <h2 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t.certificates.title}
            </h2>
          </div>
          <p className="text-xl leading-relaxed mb-8 text-red-100">
            {t.certificates.description}
          </p>          <button 
            onClick={() => setIsCertificateViewerOpen(true)}
            className="bg-white text-red-900 px-8 py-3 rounded-full font-semibold hover:bg-red-50 flex items-center gap-2">
            {t.certificates.button}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Main Content Continues */}
      <main className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto py-12">
          {/* Technologies Section */}
          <section className="py-16">
            <div className="max-w-4xl mx-auto text-center">
              <AnimatedSection animationType="fadeInUp">
                <div id="technologies" className="flex items-center justify-center gap-4 mb-6">
                  <Code className={`w-12 h-12 transition-colors duration-300 ${
                    isDarkMode ? 'text-red-400' : 'text-red-900'
                  }`} />
                  <h2
                    className={`text-4xl font-bold transition-colors duration-300 ${
                      isDarkMode ? 'text-red-400' : 'text-red-900'
                    }`}
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                  >
                    {t.technologies.title}
                  </h2>
                </div>
                <p className={`text-xl leading-relaxed mb-8 transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {t.technologies.subtitle}
                </p>
              </AnimatedSection>              <AnimatedSection animationType="scaleIn" delay={300}>
                {/* Technologies Card */}
                <div className={`w-full max-w-5xl mx-auto p-6 rounded-2xl transition-all duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-800' 
                    : 'bg-red-50'                }`} style={{
                  boxShadow: isDarkMode 
                    ? '12px 12px 0px #000000' // sombra brutalista más chica
                    : '12px 12px 0px #7f1d1d' // sombra brutalista más chica
                }}>
                  <h3 className={`text-2xl font-bold mb-6 transition-colors duration-300 ${
                    isDarkMode ? 'text-red-800' : 'text-red-900'
                  }`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {t.technologies.allTags}
                  </h3>
                  <div className="overflow-auto max-h-80">
                    <div className="flex flex-wrap gap-3 justify-center">
                      {['JavaScript',
  'Python',
  'HTML & CSS',
  'React',
  'Node.js',
  'Tailwind CSS',
  'FastAPI',
  'Pandas',
  'SQL',
  'SQLite',
  'Git & GitHub',
  'Figma',
  'Jupyter Netbooks',
  'Flask',
  'MongoDB',
  'Postman',
  'Google Cloud Platform',
  'Agentspace'
].map((tag, index) => (
  <span
    key={index}
    className={`inline-block px-6 py-3 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 relative ${
      isDarkMode
        ? 'bg-gray-700 text-white hover:bg-gray-600'
        : 'bg-red-900 text-white hover:bg-red-800'
    }`}
    style={{
      fontFamily: 'Raleway, sans-serif'
    }}
  >
    <span className={`absolute left-2 top-1/2 transform -translate-y-1/2 w-2 h-2 rounded-full ${
      isDarkMode ? 'bg-white' : 'bg-red-200'
    }`}></span>
    {tag}
    <span className={`absolute right-2 top-1/2 transform -translate-y-1/2 w-2 h-2 rounded-full ${
      isDarkMode ? 'bg-white' : 'bg-red-200'
    }`}></span>
  </span>
))}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </section>
          
        </div>
      </main>      
      <Footer language={language} isDarkMode={isDarkMode} />
        {/* Certificate Viewer Modal */}
      <CertificateViewer 
        isOpen={isCertificateViewerOpen}
        onClose={() => setIsCertificateViewerOpen(false)}
        language={language}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default Conocimientos;